import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import type { BillingAddonType, BillingServiceType } from 'webapp/enums/enums';
import { BillingCycle } from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { SubscriptionItemType } from 'webapp/models-custom/subscription';

export interface PreviewItem {
	name: string;
	priceFormatted: string;
	type: SubscriptionItemType;
	quantity: number;
	id?: BillingServiceType | BillingAddonType;
	monthlyCyclePriceFormatted?: string;
	icon?: IconName;
	subtitle?: string;
	isMostPopular?: boolean;
	isFree?: boolean; // unused?
}

interface Args {
	model: PreviewItem;
	selectedItemIds?: string[];
	isLoading?: boolean;
	forBillingCycle?: BillingCycle;
	onAction?(item: PreviewItem): void;
}

export default class BillingPreviewItemComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;

	get buttonText(): string {
		return this.isSelected ? '' : 'Add';
	}

	get buttonIcon(): IconName | undefined {
		return this.isSelected ? 'Check' : undefined;
	}

	get buttonTheme(): string | undefined {
		return this.isSelected ? undefined : 'green-outlined';
	}

	get isSelected(): boolean {
		const { selectedItemIds, model } = this.args;
		return model.id ? !!selectedItemIds?.includes(model.id) : false;
	}

	get isMonthlyCycle(): boolean {
		return this.args.forBillingCycle === BillingCycle.MONTHLY;
	}

	get showAddButton(): boolean {
		const { model } = this.args;
		return !!this.args.onAction && !!model.id && !model.isFree;
	}

	get name(): string {
		const { model } = this.args;

		if (model.type === 'plan') {
			return 'Base plan';
		}

		return model.name;
	}

	get quantityText(): string {
		const { quantity } = this.args.model;
		return quantity > 1 ? `${quantity} ` : '';
	}

	get priceFormatted(): string {
		const { model } = this.args;

		if (this.isMonthlyCycle) {
			return this.monthlyCyclePriceFormatted;
		}

		return model.priceFormatted;
	}

	get monthlyCyclePriceFormatted(): string {
		const monthlyCyclePriceFormatted = this.args.model.monthlyCyclePriceFormatted;

		if (monthlyCyclePriceFormatted) {
			const cycleText = '/mo';
			if (this.isMonthlyCycle) {
				return `${monthlyCyclePriceFormatted}<div>${cycleText}</div>`;
			} else {
				return `(${monthlyCyclePriceFormatted}${cycleText})`;
			}
		} else {
			return '';
		}
	}

	get showMonthlyCyclePrice(): boolean {
		return !this.isMonthlyCycle;
	}

	@action
	onAction(): void {
		this.args.onAction?.(this.args.model);
	}
	<template>
		<div class="preview-item">
			<div class="preview-item__header">
				{{#if @model.icon}}
					<CommonUiIconComponent
						@name={{@model.icon}}
						@size="lg"
						class="preview-item__icon"
					/>
				{{/if}}
				<div class="preview-item__title">{{this.quantityText}}{{this.name}}</div>
				{{#if @model.subtitle}}
					<div class="preview-item__subtitle">{{@model.subtitle}}</div>
				{{/if}}
				{{#if @model.isMostPopular}}
					<div class="preview-item__most-popular">Most popular</div>
				{{/if}}
			</div>
			<div class="preview-item__footer">
				<div class="preview-item__prices">
					{{#if this.showMonthlyCyclePrice}}
						<div class="preview-item__price preview-item__price--monthly">
							{{{this.monthlyCyclePriceFormatted}}}
						</div>
					{{/if}}
					<div class="preview-item__price preview-item__price--main">
						{{{this.priceFormatted}}}
					</div>
				</div>
				{{#if this.showAddButton}}
					<SubmitButtonComponent
						@text={{this.buttonText}}
						@icon={{this.buttonIcon}}
						@theme={{this.buttonTheme}}
						@extraClass="preview-item__action"
						@noDelay={{true}}
						@submitInProgress={{@isLoading}}
						@onTrigger={{this.onAction}}
					/>
				{{else if @model.isFree}}
					<div class="button size-normal preview-item__action preview-item__action--free">
						<CommonUiIconComponent @name="Check" @size="sm" />
						Free
					</div>
				{{/if}}

			</div>
		</div>
	</template>
}
